import {Component, Inject, OnInit} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {FacilityInventoryProduct} from '../../../../../model/FacilityInventoryProduct';
import {Product} from '../../../../../model/Product';
import {FacilityInventoryProductService} from '../../../../service-data/facility-inventory-product.service';
import {ProductService} from '../../../../service-data/product.service';
import {ServerErrorToastService} from '../../../../service-ui/server-error-toast.service';
import {LanguagePipe} from '../../../../pipe/language.pipe';

/**
 * "Add the Product to an Existing Inventory Item".
 *
 * A Book that has no FacilityInventoryProduct of its own can still be
 * stocked when another Book *in the same Project* is already carried in
 * inventory - e.g. a reprint, a new ISBN, or a cover refresh of the same
 * title. Rather than opening a second inventory item (a second shelf
 * location, a second reorder level, a second replenishment cadence) for
 * what the facility treats as one stack of books, the new Book's Product
 * is appended to the existing FacilityInventoryProduct's `productIds`
 * list. That list is exactly what
 * `PlaceReplenishmentOrderComponent` offers when a replenishment order is
 * placed, so the operator can then reorder either edition against the one
 * inventory item.
 *
 * Note this deliberately does NOT change `product_ID` - the primary
 * product of the inventory item stays whatever it was. Promoting the new
 * edition to primary is a separate decision made on the inventory item
 * itself.
 *
 * All user-visible text lives in the template. A string read from
 * TypeScript via `LanguagePipe.get(...)` only resolves at runtime when the
 * same id is ALSO extracted from some template (see the note at the top of
 * `tools/sync-i18n-strings.js`), so the status lines are template-driven
 * booleans and the two toast ids below are either rendered by this
 * template as well or already present in every bundle.
 */
@Component({
  selector: 'app-add-product-to-existing-inventory-item',
  templateUrl: './add-product-to-existing-inventory-item-component.html',
  styleUrls: ['./add-product-to-existing-inventory-item-component.css']
})
export class AddProductToExistingInventoryItemComponent implements OnInit {
  public bookId: number = 0;
  public book: any;
  public product: Product;
  public candidates: any[] = [];
  public formGroup: FormGroup;

  /** Waiting on the server for the Product behind `bookId`. */
  public looking: boolean = false;
  /** No Product exists for `bookId` - nothing can be added. */
  public productNotFound: boolean = false;
  /** The last save attempt was rejected; the dialog stays open. */
  public saveFailed: boolean = false;
  public saving: boolean = false;

  private facilityInventoryProducts: any[];

  /**
   * The Project a Book belongs to.
   *
   * `BookService.getLinkData()` links `book.work_ID` to the
   * ProjectService, so `work_ID` IS the Project id - the name is legacy.
   * The linked `project` object is only present once the ProjectService
   * has that Project loaded, which is not the case on the manufacturing
   * screens, so `work_ID` is the reliable half and `project.ID` is only a
   * confirmation.
   */
  public static getProjectId(book: any): number {
    if ( !book ) { return 0; }
    if ( book.project && book.project.ID ) { return Number(book.project.ID); }
    return Number(book.work_ID || 0);
  }

  /**
   * FacilityInventoryProducts that could absorb `book`'s Product: an
   * inventory item for a DIFFERENT Book that sits in the same Project and
   * the same organization.
   *
   * Shared with the manufacturing bucket so the button that opens this
   * dialog and the dialog itself can never disagree about whether there
   * is anything to pick.
   */
  public static findCandidates(book: any, bookId: number, facilityInventoryProducts: any[]): any[] {
    const projectId = AddProductToExistingInventoryItemComponent.getProjectId(book);
    if ( !projectId ) { return []; }
    const orgId = Number((book && book.org_ID) || 0);
    const candidates: any[] = [];
    for ( const facilityInventoryProduct of (facilityInventoryProducts || []) ) {
      const candidateProduct = facilityInventoryProduct ? facilityInventoryProduct.product : undefined;
      const candidateBook = candidateProduct ? candidateProduct.book : undefined;
      if ( !candidateProduct || !candidateBook ) { continue; }
      // An inventory item for this very Book is not a candidate - that
      // case is "this book is already stocked" and no button is offered.
      if ( Number(candidateBook.ID) === Number(bookId) ) { continue; }
      if ( AddProductToExistingInventoryItemComponent.getProjectId(candidateBook) !== projectId ) { continue; }
      const candidateOrgId = Number(candidateProduct.org_ID || candidateBook.org_ID || 0);
      if ( orgId && candidateOrgId && candidateOrgId !== orgId ) { continue; }
      candidates.push(facilityInventoryProduct);
    }
    return candidates;
  }

  constructor(private formBuilder: FormBuilder,
              public productService: ProductService,
              public facilityInventoryProductService: FacilityInventoryProductService,
              private serverErrorToastService: ServerErrorToastService,
              @Inject(MAT_DIALOG_DATA) data,
              private dialogRef: MatDialogRef<AddProductToExistingInventoryItemComponent>) {
    this.book = data.book;
    this.bookId = Number(data.bookId || (data.book ? data.book.ID : 0) || 0);
    this.facilityInventoryProducts = data.facilityInventoryProducts || this.facilityInventoryProductService.objects;
  }

  public ngOnInit(): void {
    this.formGroup = this.formBuilder.group({
      facilityInventoryProduct_ID: [0, [Validators.required, Validators.min(1)]]
    });
    this.candidates = AddProductToExistingInventoryItemComponent.findCandidates(this.book, this.bookId, this.facilityInventoryProducts);
    this.lookupProduct();
  }

  /**
   * Resolve the Product for `bookId`, preferring a live (non archived)
   * one. Mirrors `AddFacilityInventoryProductComponent.lookupProductFromBookId`:
   * the local cache usually has it, and only falls back to the server when
   * it does not.
   */
  private lookupProduct(): void {
    this.productNotFound = false;
    for ( const product of this.productService.objects ) {
      if ( Number(product.book_ID) === Number(this.bookId) ) {
        if ( !this.product || Product.isArchived(this.product) ) {
          this.product = product;
        }
      }
    }
    if ( this.product ) {
      this.preselectSingleCandidate();
      return;
    }
    this.looking = true;
    this.productService.getFilteredItems({ eq: { 'product.book_ID': this.bookId } }, true, 10).subscribe( products => {
      this.looking = false;
      if ( products && products.length > 0 ) {
        this.product = products[0];
        for ( const product of products ) {
          if ( !Product.isArchived(product) ) {
            this.product = product;
          }
        }
        this.preselectSingleCandidate();
      } else {
        this.productNotFound = true;
      }
    });
  }

  /** With one choice there is nothing to choose - pre-select it. */
  private preselectSingleCandidate(): void {
    const selectable = this.selectableCandidates;
    if ( selectable.length === 1 ) {
      this.formGroup.controls.facilityInventoryProduct_ID.setValue(selectable[0].ID);
    }
  }

  /** The inventory items this Product is not already part of. */
  public get selectableCandidates(): any[] {
    if ( !this.product ) { return this.candidates; }
    return this.candidates.filter( candidate => !this.alreadyContainsProduct(candidate) );
  }

  public alreadyContainsProduct(facilityInventoryProduct: any): boolean {
    if ( !this.product || !facilityInventoryProduct ) { return false; }
    return this.splitProductIds(facilityInventoryProduct).indexOf(Number(this.product.ID)) !== -1;
  }

  private splitProductIds(facilityInventoryProduct: any): number[] {
    const ids: number[] = [];
    const raw = facilityInventoryProduct && facilityInventoryProduct.productIds ? String(facilityInventoryProduct.productIds) : '';
    for ( const part of raw.split(',') ) {
      const id = Number(part);
      if ( !isNaN(id) && id > 0 && ids.indexOf(id) === -1 ) {
        ids.push(id);
      }
    }
    // Legacy rows occasionally omit the primary product from productIds.
    const primary = Number(facilityInventoryProduct ? facilityInventoryProduct.product_ID : 0);
    if ( primary > 0 && ids.indexOf(primary) === -1 ) {
      ids.unshift(primary);
    }
    return ids;
  }

  public getTitle(facilityInventoryProduct: any): string {
    if ( !facilityInventoryProduct ) { return ''; }
    if ( facilityInventoryProduct.title ) { return facilityInventoryProduct.title; }
    if ( facilityInventoryProduct.product && facilityInventoryProduct.product.title ) { return facilityInventoryProduct.product.title; }
    return '';
  }

  public onAdd(): void {
    this.saveFailed = false;
    const selectedId = Number(this.formGroup.controls.facilityInventoryProduct_ID.value);
    const selected = this.candidates.find( candidate => Number(candidate.ID) === selectedId );
    if ( !selected || !this.product ) {
      this.productNotFound = !this.product;
      return;
    }
    // Update the service's own instance where there is one - that is the
    // object the rest of the UI is bound to, and `updateItem` serializes
    // whatever object it is handed.
    const facilityInventoryProduct: FacilityInventoryProduct =
      this.facilityInventoryProductService.getLocalItem(selectedId) || (selected as FacilityInventoryProduct);
    const productIds = this.splitProductIds(facilityInventoryProduct);
    if ( productIds.indexOf(Number(this.product.ID)) === -1 ) {
      productIds.push(Number(this.product.ID));
    }
    const previousProductIds = facilityInventoryProduct.productIds;
    facilityInventoryProduct.productIds = productIds.join(',');
    facilityInventoryProduct.lastUpdate = new Date();
    this.saving = true;
    this.facilityInventoryProductService.updateItem(facilityInventoryProduct).subscribe(
      updated => {
        this.saving = false;
        this.serverErrorToastService.reportSuccess(LanguagePipe.get('Saved.'));
        this.dialogRef.close(updated);
      },
      error => {
        this.saving = false;
        this.saveFailed = true;
        // Put the list back so the screen does not claim a change that
        // never reached the server.
        facilityInventoryProduct.productIds = previousProductIds;
        this.serverErrorToastService.reportError('addProductToExistingInventoryItem', error,
          LanguagePipe.get('The Product could not be added to the inventory item.'));
      }
    );
  }
}

